// ─────────────────────────────────────────────────────────────────────────────
//  hooks/usePermissions.js — Pattern 2: Fixed roles + customizable matrix
//
//  Fetches the per-business permission matrix from the backend, so the owner
//  can customize what each role sees/does via the Settings > Permissions grid.
//  Falls back to hardcoded defaults while loading or on error.
//
//  Usage:
//    const perms = usePermissions();
//    {perms.createSale && <button ...>}      // action gating
//    {perms.viewSales && <SalesTab />}        // tab visibility
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { fetchPermissionMatrix } from '../services/queries';
import { updateRolePermission } from '../services/commands';

// ── Permission key → UI capability mapping ──────────────────────────────────
// Maps the backend permission keys (patch 020/021) to the flat boolean flags
// the UI reads. Each entry: [backendKey, uiFlagName]
const PERM_MAP = [
  // View permissions (tab visibility)
  ['dashboard.view',  'viewDashboard'],
  ['sale.view',       'viewSales'],
  ['purchase.view',   'viewPurchases'],
  ['expense.view',    'viewExpenses'],
  ['service.view',    'viewServices'],
  ['inventory.view',  'viewInventory'],
  ['cash.view',       'viewCash'],
  ['report.view',     'viewReports'],
  ['settings.view',   'viewSettings'],
  ['team.view',       'viewTeam'],
  // Master data
  ['master.upsert',   'masterData'],
  ['master.archive',  'archiveMasterData'],
  // Sales
  ['sale.create',     'createSale'],
  ['sale.post',       'postSale'],
  ['sale.void',       'voidSale'],
  ['payment.record',  'recordPayment'],
  // Purchases
  ['purchase.create',        'createPurchase'],
  ['purchase.post',          'postPurchase'],
  ['purchase.void',          'voidPurchase'],
  ['purchase.payment.record','recordPurchasePayment'],
  // Expenses
  ['expense.create',         'createExpense'],
  ['expense.post',           'postExpense'],
  ['expense.void',           'voidExpense'],
  ['expense.payment.record', 'recordExpensePayment'],
  // Services
  ['service.create',         'createService'],
  ['service.post',           'postService'],
  ['service.void',           'voidService'],
  ['service.payment.record', 'recordServicePayment'],
  // Returns
  ['sales_return.create',    'createSalesReturn'],
  ['sales_return.post',      'postSalesReturn'],
  ['sales_return.void',      'voidSalesReturn'],
  ['purchase_return.create', 'createPurchaseReturn'],
  ['purchase_return.post',   'postPurchaseReturn'],
  ['purchase_return.void',   'voidPurchaseReturn'],
  // Invoices
  ['invoice.create',  'createInvoice'],
  ['invoice.post',    'postInvoice'],
  ['invoice.void',    'voidInvoice'],
  ['invoice.deposit', 'invoiceDeposit'],
  ['invoice.convert', 'invoiceConvert'],
  // Cash / wallet
  ['cash.adjust',   'cashAdjust'],
  ['cash.transfer',  'cashTransfer'],
  ['cash.reverse',   'cashReverse'],
  ['wallet.reverse', 'walletReverse'],
  // Team
  ['member.invite',       'inviteMembers'],
  ['member.manage',       'manageMembers'],
  ['ownership.transfer',  'transferOwnership'],
];

// ── Hardcoded defaults (used while loading / on error) ──────────────────────
const ALL_TRUE = Object.fromEntries(PERM_MAP.map(([, ui]) => [ui, true]));
const ALL_FALSE = Object.fromEntries(PERM_MAP.map(([, ui]) => [ui, false]));

const DEFAULTS = {
  OWNER: { ...ALL_TRUE },
  ADMIN: { ...ALL_TRUE, transferOwnership: false },
  ACCOUNTANT: {
    ...ALL_TRUE,
    inviteMembers: false, manageMembers: false, transferOwnership: false,
    viewSettings: false, viewTeam: false,
    editBusinessDetails: false,
  },
  MANAGER: {
    ...ALL_TRUE,
    voidSale: false, voidPurchase: false, voidExpense: false, voidService: false,
    voidSalesReturn: false, voidPurchaseReturn: false, voidInvoice: false,
    archiveMasterData: false,
    cashAdjust: false, cashTransfer: false, cashReverse: false, walletReverse: false,
    viewCash: false,
    inviteMembers: false, manageMembers: false, transferOwnership: false,
    viewSettings: false, viewTeam: false,
    editBusinessDetails: false,
  },
  CASHIER: {
    ...ALL_FALSE,
    viewDashboard: true, viewSales: true, viewServices: true,
    createSale: true, postSale: true,
    createService: true, postService: true,
    recordPayment: true, recordServicePayment: true,
    viewReports: false,
  },
  VIEWER: {
    ...ALL_FALSE,
    viewDashboard: true, viewSales: true, viewPurchases: true,
    viewExpenses: true, viewServices: true, viewInventory: true,
    viewCash: true, viewReports: true,
  },
};

// Convert backend matrix row (keyed by permission.key) to UI flags
function matrixRowToFlags(backendRow) {
  const flags = { ...ALL_FALSE };
  for (const [backendKey, uiFlag] of PERM_MAP) {
    if (backendRow[backendKey] === true) flags[uiFlag] = true;
  }
  // Always add the composite editBusinessDetails flag
  flags.editBusinessDetails = flags.viewSettings;
  return flags;
}

export function usePermissions() {
  const { userRole, isOwner, businessId, token } = useBusiness();
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the permission matrix from the backend
  useEffect(() => {
    if (!businessId || !token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPermissionMatrix(businessId, token);
        if (!cancelled && data) setMatrixData(data);
      } catch (err) {
        console.warn('[usePermissions] Failed to fetch matrix, using defaults:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId, token]);

  // Current user's resolved permissions
  const perms = useMemo(() => {
    if (isOwner) return DEFAULTS.OWNER;

    // If we have live matrix data, use it
    if (matrixData?.matrix && userRole && matrixData.matrix[userRole]) {
      return matrixRowToFlags(matrixData.matrix[userRole]);
    }

    // Fallback to hardcoded defaults
    return DEFAULTS[userRole] || ALL_FALSE;
  }, [isOwner, userRole, matrixData]);

  return perms;
}

// ── Hook for the permissions grid UI (owner only) ───────────────────────────
export function usePermissionMatrix() {
  const { businessId, token, isOwner } = useBusiness();
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!businessId || !token) return;
    setLoading(true);
    try {
      const data = await fetchPermissionMatrix(businessId, token);
      if (data) setMatrixData(data);
    } catch (err) {
      console.warn('[usePermissionMatrix] Load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [businessId, token]);

  useEffect(() => { reload(); }, [reload]);

  const togglePermission = useCallback(async (roleKey, permissionKey, granted) => {
    if (!isOwner || !businessId || !token) return;
    // Optimistic update first — checkbox flips instantly
    setMatrixData(prev => {
      if (!prev?.matrix) return prev;
      return {
        ...prev,
        matrix: {
          ...prev.matrix,
          [roleKey]: { ...prev.matrix[roleKey], [permissionKey]: granted },
        },
      };
    });
    try {
      await updateRolePermission(businessId, roleKey, permissionKey, granted, token);
    } catch (err) {
      console.error('[usePermissionMatrix] Toggle failed:', err.message);
      // Revert to server state on failure
      await reload();
    }
  }, [isOwner, businessId, token, reload]);

  return { matrixData, loading, saving, togglePermission, reload };
}

// ── Permission grid structure (for the UI) ──────────────────────────────────
// Groups permissions into feature areas with columns for View/Create/Post/Void
export const PERMISSION_GRID = [
  {
    area: 'Dashboard',
    areaKey: 'dashboard',
    permissions: [
      { key: 'dashboard.view', label: 'View', column: 'View' },
    ],
  },
  {
    area: 'Sales',
    areaKey: 'sales',
    permissions: [
      { key: 'sale.view',    label: 'View',    column: 'View' },
      { key: 'sale.create',  label: 'Create',  column: 'Create' },
      { key: 'sale.post',    label: 'Post',    column: 'Post' },
      { key: 'sale.void',    label: 'Void',    column: 'Void' },
      { key: 'payment.record', label: 'Record Payment', column: 'Pay' },
    ],
  },
  {
    area: 'Purchases',
    areaKey: 'purchases',
    permissions: [
      { key: 'purchase.view',           label: 'View',    column: 'View' },
      { key: 'purchase.create',         label: 'Create',  column: 'Create' },
      { key: 'purchase.post',           label: 'Post',    column: 'Post' },
      { key: 'purchase.void',           label: 'Void',    column: 'Void' },
      { key: 'purchase.payment.record', label: 'Record Payment', column: 'Pay' },
    ],
  },
  {
    area: 'Expenses',
    areaKey: 'expenses',
    permissions: [
      { key: 'expense.view',           label: 'View',    column: 'View' },
      { key: 'expense.create',         label: 'Create',  column: 'Create' },
      { key: 'expense.post',           label: 'Post',    column: 'Post' },
      { key: 'expense.void',           label: 'Void',    column: 'Void' },
      { key: 'expense.payment.record', label: 'Record Payment', column: 'Pay' },
    ],
  },
  {
    area: 'Services',
    areaKey: 'services',
    permissions: [
      { key: 'service.view',           label: 'View',    column: 'View' },
      { key: 'service.create',         label: 'Create',  column: 'Create' },
      { key: 'service.post',           label: 'Post',    column: 'Post' },
      { key: 'service.void',           label: 'Void',    column: 'Void' },
      { key: 'service.payment.record', label: 'Record Payment', column: 'Pay' },
    ],
  },
  {
    area: 'Inventory',
    areaKey: 'inventory',
    permissions: [
      { key: 'inventory.view', label: 'View', column: 'View' },
    ],
  },
  {
    area: 'Invoices',
    areaKey: 'invoices',
    permissions: [
      { key: 'invoice.create',  label: 'Create',  column: 'Create' },
      { key: 'invoice.post',    label: 'Post',    column: 'Post' },
      { key: 'invoice.void',    label: 'Void',    column: 'Void' },
      { key: 'invoice.deposit', label: 'Deposit',  column: 'Pay' },
      { key: 'invoice.convert', label: 'Convert',  column: 'Other' },
    ],
  },
  {
    area: 'Returns',
    areaKey: 'returns',
    permissions: [
      { key: 'sales_return.create',    label: 'Sales Return',    column: 'Create' },
      { key: 'sales_return.post',      label: 'Post Sales Ret.',  column: 'Post' },
      { key: 'purchase_return.create', label: 'Purchase Return',  column: 'Create' },
      { key: 'purchase_return.post',   label: 'Post Purch. Ret.', column: 'Post' },
    ],
  },
  {
    area: 'Cash & Accounts',
    areaKey: 'cash',
    permissions: [
      { key: 'cash.view',     label: 'View',     column: 'View' },
      { key: 'cash.adjust',   label: 'Adjust',   column: 'Create' },
      { key: 'cash.transfer', label: 'Transfer',  column: 'Create' },
      { key: 'cash.reverse',  label: 'Reverse',   column: 'Void' },
    ],
  },
  {
    area: 'Master Data',
    areaKey: 'master',
    permissions: [
      { key: 'master.upsert',  label: 'Create/Edit', column: 'Create' },
      { key: 'master.archive', label: 'Archive',      column: 'Void' },
    ],
  },
  {
    area: 'Reports',
    areaKey: 'reports',
    permissions: [
      { key: 'report.view', label: 'View', column: 'View' },
    ],
  },
  {
    area: 'Settings',
    areaKey: 'settings',
    permissions: [
      { key: 'settings.view', label: 'View', column: 'View' },
    ],
  },
  {
    area: 'Team',
    areaKey: 'team',
    permissions: [
      { key: 'team.view',          label: 'View',    column: 'View' },
      { key: 'member.invite',      label: 'Invite',  column: 'Create' },
      { key: 'member.manage',      label: 'Manage',  column: 'Other' },
      { key: 'ownership.transfer', label: 'Transfer Ownership', column: 'Other' },
    ],
  },
];

// Role display info
export const ROLE_INFO = {
  OWNER:      { label: 'Owner',      description: 'Full access to everything' },
  ADMIN:      { label: 'Admin',      description: 'Full access except ownership transfer' },
  ACCOUNTANT: { label: 'Accountant', description: 'All financial operations, no team management' },
  MANAGER:    { label: 'Manager',    description: 'Create and post transactions, manage products' },
  CASHIER:    { label: 'Cashier',    description: 'Sales and service transactions only' },
  VIEWER:     { label: 'Viewer',     description: 'Read-only access to all data' },
};
